import React, { useState, useEffect } from 'react';
import { 
  X, 
  ExternalLink, 
  ShieldAlert, 
  Layers, 
  Server, 
  History, 
  CheckCircle, 
  UserCheck, 
  ArrowRight, 
  Activity, 
  FileText 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';

interface IncidentAlert {
  id: string;
  alert_id: string;
  alert_name: string;
  provider: string;
  severity: string;
  status: string;
  description: string;
  triggered_at: string;
}

interface IncidentEntity {
  id: string;
  entity_type: string;
  entity_value: string;
  details?: any;
}

interface AuditLog {
  id: string;
  action: string;
  module: string;
  description: string;
  created_at: string;
  performed_by_name?: string;
  metadata?: any;
}

interface SentinelIncidentDetailModalProps {
  incidentId: string;
  onClose: () => void;
  onOpenUseCase?: (useCaseId: string) => void;
  onIncidentUpdated?: () => void;
}

export function SentinelIncidentDetailModal({
  incidentId,
  onClose,
  onOpenUseCase,
  onIncidentUpdated
}: SentinelIncidentDetailModalProps) {
  const { session } = useAuth();
  const [incident, setIncident] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'ALERTS' | 'ENTITIES' | 'WORKFLOW' | 'AUDIT'>('OVERVIEW');
  const [assigneeName, setAssigneeName] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [statusComments, setStatusComments] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/soc/sentinel/incidents/${incidentId}`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setIncident(data);
        setStatusUpdate(data.status || 'Active');
      }
    } catch (e) {
      console.error('Failed to fetch incident details:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (incidentId && session?.access_token) {
      fetchDetails();
    }
  }, [incidentId, session?.access_token]);

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assigneeName) return;
    try {
      setIsAssigning(true);
      const engId = '22222222-2222-2222-2222-222222222222';
      const res = await fetch(`/api/soc/incidents/${incident.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          engineer_id: engId,
          engineer_name: assigneeName
        })
      });
      if (res.ok) {
        setActionSuccess(`Assigned engineer "${assigneeName}" successfully.`);
        fetchDetails();
        if (onIncidentUpdated) onIncidentUpdated();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsUpdatingStatus(true);
      const res = await fetch(`/api/soc/incidents/${incident.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          status: statusUpdate,
          comments: statusComments
        })
      });
      if (res.ok) {
        setActionSuccess(`Incident status updated to ${statusUpdate}.`);
        fetchDetails();
        if (onIncidentUpdated) onIncidentUpdated();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getSeverityBadge = (sev: string) => {
    const s = (sev || 'medium').toLowerCase();
    if (s === 'critical') return 'bg-rose-500/10 text-rose-500 border-rose-500/30';
    if (s === 'high') return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
    if (s === 'low' || s === 'informational') return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';
    return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-sm font-medium text-foreground">Loading Incident Details...</p>
        </div>
      </div>
    );
  }

  if (!incident) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-border bg-muted/20 flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <span className="px-2.5 py-1 text-xs font-mono font-bold rounded-lg bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">
                {incident.external_incident_id}
              </span>
              <span className={cn("px-2.5 py-1 text-xs font-bold rounded-lg border", getSeverityBadge(incident.severity))}>
                {incident.severity}
              </span>
              <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-secondary text-secondary-foreground border border-border">
                {incident.status || 'Active'}
              </span>
              <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20">
                SOC Team
              </span>
            </div>
            <h2 className="text-xl font-bold text-foreground mt-2">{incident.title}</h2>
            <p className="text-xs text-muted-foreground">
              Source: <span className="font-semibold text-foreground">Microsoft Sentinel</span> • Ingested: {new Date(incident.created_at).toLocaleString()}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {incident.sentinel_url && (
              <a 
                href={incident.sentinel_url} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl bg-secondary text-secondary-foreground hover:bg-muted border border-border transition-colors"
                title="Open in Microsoft Azure Sentinel Portal"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Sentinel Portal
              </a>
            )}
            <button 
              onClick={onClose}
              className="p-1.5 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Action Success Alert */}
        {actionSuccess && (
          <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-6 py-2.5 text-xs text-emerald-400 flex items-center justify-between">
            <span>{actionSuccess}</span>
            <button onClick={() => setActionSuccess(null)} className="hover:text-foreground">✕</button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-border bg-muted/10 px-6 gap-2">
          {[
            { id: 'OVERVIEW', label: 'Overview & Details', icon: FileText },
            { id: 'ALERTS', label: `Alerts (${incident.alerts?.length || 0})`, icon: ShieldAlert },
            { id: 'ENTITIES', label: `Entities (${incident.entities?.length || 0})`, icon: Server },
            { id: 'WORKFLOW', label: '15-Stage Workflow', icon: Layers },
            { id: 'AUDIT', label: `Audit History (${incident.audit_logs?.length || 0})`, icon: History }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-all",
                  isActive 
                    ? "border-indigo-500 text-indigo-400 bg-indigo-500/5" 
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Modal Body */}
        <div className="p-6 max-h-[60vh] overflow-y-auto space-y-6">
          
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'OVERVIEW' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-6">
                
                <div className="space-y-2">
                  <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Description & Evidence</h3>
                  <div className="bg-muted/30 border border-border/60 rounded-xl p-4 text-xs leading-relaxed text-foreground whitespace-pre-line">
                    {incident.description || 'No description provided in raw payload.'}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/20 border border-border/50 rounded-xl p-3.5">
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase">MITRE ATT&CK Tactic</span>
                    <p className="text-sm font-semibold text-foreground mt-1">{incident.mitre_tactic || 'Initial Access'}</p>
                  </div>
                  <div className="bg-muted/20 border border-border/50 rounded-xl p-3.5">
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase">MITRE Technique</span>
                    <p className="text-sm font-semibold text-foreground mt-1">{incident.mitre_technique || 'T1190 - Exploit Public-Facing Application'}</p>
                  </div>
                </div>

                <div className="bg-muted/20 border border-border/50 rounded-xl p-4">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase">Detection Logic & Rule</span>
                  <p className="text-sm font-semibold text-foreground mt-1 font-mono text-indigo-400">
                    {incident.detection_rule || incident.title}
                  </p>
                </div>

                {/* Auto Created Use Case Banner */}
                {incident.use_case_number && (
                  <div className="p-4 rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-500/10 via-purple-500/5 to-transparent flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-bold text-foreground">SOC Use Case Auto-Created (Step 1)</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Use Case <span className="font-mono font-bold text-indigo-400">{incident.use_case_number}</span> is actively tracking this incident in the 15-stage workflow.
                      </p>
                    </div>
                    {onOpenUseCase && incident.use_case_id && (
                      <button
                        onClick={() => onOpenUseCase(incident.use_case_id)}
                        className="px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
                      >
                        Open Use Case <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar Quick Actions */}
              <div className="space-y-6">
                
                {/* Assign Engineer Card */}
                <div className="bg-muted/20 border border-border/60 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <UserCheck className="w-3.5 h-3.5 text-indigo-400" /> Assign Engineer
                  </h4>
                  <p className="text-[11px] text-muted-foreground">
                    Assign a SOC Engineer to investigate. Note: One active task per engineer rule applies.
                  </p>
                  <form onSubmit={handleAssign} className="space-y-2.5">
                    <input
                      type="text"
                      placeholder="Engineer name (e.g. Akaruppasamy)"
                      value={assigneeName}
                      onChange={e => setAssigneeName(e.target.value)}
                      className="w-full text-xs bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                    />
                    <button
                      type="submit"
                      disabled={isAssigning || !assigneeName}
                      className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      {isAssigning ? 'Assigning...' : 'Assign Engineer'}
                    </button>
                  </form>
                  {incident.assigned_engineer_name && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Currently Assigned: <span className="font-semibold text-foreground">{incident.assigned_engineer_name}</span>
                    </div>
                  )}
                </div>

                {/* Update Status Card */}
                <div className="bg-muted/20 border border-border/60 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5 text-indigo-400" /> Update Status
                  </h4>
                  <form onSubmit={handleUpdateStatus} className="space-y-2.5">
                    <select
                      value={statusUpdate}
                      onChange={e => setStatusUpdate(e.target.value)}
                      className="w-full text-xs bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                    >
                      <option value="New">New</option>
                      <option value="Active">Active / In Progress</option>
                      <option value="Investigating">Investigating</option>
                      <option value="Closed">Closed</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Audit transition comments..."
                      value={statusComments}
                      onChange={e => setStatusComments(e.target.value)}
                      className="w-full text-xs bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-indigo-500"
                    />
                    <button
                      type="submit"
                      disabled={isUpdatingStatus}
                      className="w-full py-2 rounded-lg bg-secondary hover:bg-muted text-secondary-foreground text-xs font-semibold border border-border transition-colors flex items-center justify-center gap-1.5"
                    >
                      Update Status
                    </button>
                  </form>
                </div>

              </div>
            </div>
          )}

          {/* TAB 2: ALERTS */}
          {activeTab === 'ALERTS' && (
            <div className="space-y-3">
              {incident.alerts && incident.alerts.length > 0 ? (
                incident.alerts.map((alt: IncidentAlert) => (
                  <div key={alt.id} className="p-4 rounded-xl border border-border/60 bg-muted/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-amber-500" />
                        <span className="text-xs font-bold text-foreground">{alt.alert_name}</span>
                        <span className="text-[11px] font-mono text-muted-foreground">({alt.alert_id})</span>
                      </div>
                      <span className={cn("px-2 py-0.5 text-[10px] font-bold rounded border", getSeverityBadge(alt.severity))}>
                        {alt.severity}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{alt.description || 'No extra alert telemetry.'}</p>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      Provider: {alt.provider} • Triggered: {new Date(alt.triggered_at || incident.created_at).toLocaleString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-xs text-muted-foreground">
                  No individual alerts parsed in payload. High-level incident alert applies.
                </div>
              )}
            </div>
          )}

          {/* TAB 3: ENTITIES */}
          {activeTab === 'ENTITIES' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {incident.entities && incident.entities.length > 0 ? (
                incident.entities.map((ent: IncidentEntity) => (
                  <div key={ent.id} className="p-4 rounded-xl border border-border/60 bg-muted/20 flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                      <Server className="w-4 h-4" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-foreground font-mono">{ent.entity_value}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-secondary text-secondary-foreground">
                          {ent.entity_type}
                        </span>
                      </div>
                      {ent.details && Object.keys(ent.details).length > 0 && (
                        <pre className="text-[10px] text-muted-foreground bg-background/50 p-2 rounded border border-border font-mono overflow-x-auto">
                          {JSON.stringify(ent.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 p-8 text-center text-xs text-muted-foreground">
                  No entities extracted from raw Sentinel payload.
                </div>
              )}
            </div>
          )}

          {/* TAB 4: 15-STAGE WORKFLOW */}
          {activeTab === 'WORKFLOW' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-xs text-indigo-300">
                Sentinel-generated incidents enter the standard 15-stage SOC Use Case workflow at <strong>Step 1: CREATED</strong>.
                Stage progression adheres strictly to the existing governance rules without bypass.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {[
                  { step: 1, name: 'CREATED', status: 'Active (Current)', color: 'border-indigo-500 bg-indigo-500/10 text-indigo-400' },
                  { step: 2, name: 'VALIDATED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 3, name: 'COORDINATION_PENDING', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 4, name: 'SHIFT_LEAD_ASSIGNMENT', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 5, name: 'IN_PROGRESS', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 6, name: 'SHIFT_LEAD_VERIFICATION', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 7, name: 'SHIFT_LEAD_REVIEW', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 8, name: 'TECHNICAL_REVIEW', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 9, name: 'UAT_APPROVED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 10, name: 'UAT_DEPLOYED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 11, name: 'WEEKLY_REVIEW_COMPLETED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 12, name: 'POST_MEETING_REPORT', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 13, name: 'FINAL_APPROVED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 14, name: 'PRODUCTION_APPROVED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' },
                  { step: 15, name: 'PRODUCTION_DEPLOYED', status: 'Pending', color: 'border-border/60 bg-muted/10 text-muted-foreground' }
                ].map(s => (
                  <div key={s.step} className={cn("p-3 rounded-xl border flex flex-col justify-between", s.color)}>
                    <div className="flex items-center justify-between text-[10px] font-mono">
                      <span>Step {s.step}</span>
                      {s.step === 1 && <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />}
                    </div>
                    <div className="text-xs font-bold mt-1 leading-tight">{s.name}</div>
                    <div className="text-[10px] mt-2 opacity-80">{s.status}</div>
                  </div>
                ))}
              </div>

              {incident.use_case_id && onOpenUseCase && (
                <div className="pt-2 text-center">
                  <button
                    onClick={() => onOpenUseCase(incident.use_case_id)}
                    className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold inline-flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all"
                  >
                    Open Master 15-Stage Stepper <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: AUDIT HISTORY */}
          {activeTab === 'AUDIT' && (
            <div className="space-y-3">
              {incident.audit_logs && incident.audit_logs.length > 0 ? (
                incident.audit_logs.map((log: AuditLog) => (
                  <div key={log.id} className="p-3.5 rounded-xl border border-border/50 bg-muted/20 flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-foreground font-mono">{log.action}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-mono">
                          {log.module}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{log.description}</p>
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-xs text-muted-foreground">
                  No explicit audit logs found matching this incident number.
                </div>
              )}
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            SOC Team Access Restricted • Security Incident UUID: <span className="font-mono text-foreground">{incident.id}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-secondary text-secondary-foreground hover:bg-muted border border-border transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
